'use client'

import { useContext, useMemo, useState } from 'react'

import FilterProductFormMenuOrderBy from '../FilterProductFormMenuOrderBy'
import FilterProductFormPriceController from '../FilterProductFormPriceController'
import FilterProductFormDesktopMenuFilterBy from './FilterProductFormDesktopMenuFilterBy'
import InputCheckbox from '../../Form/InputCheckbox'
import { ProductFilterFormContext } from '../FilterUtils/ProductFilterFormContext'
import { useTranslation } from '../../../context/ConfigContext'
import Icon from '../../Icon/Icon'
import { Icons } from '../../Icon/constants'
import { AlgoliaConfigContext } from '../../AlgoliaConfigContext/AlgoliaConfigContextProvider'
import { getIsArabicLocale } from '../../../utils/locale.utils'
import useCurrentLocale from '../../../../core/hook/useCurrentLocale'
import { AggregationsFilters } from '../../../../domain/cmsCategory'

const FilterProductFormFieldWrapper = ({ name, children }) => {
  const currentLocale = useCurrentLocale()
  const isArabicStore = getIsArabicLocale(currentLocale)

  const style = useMemo(() => {
    if (isArabicStore) {
      const parentWidth =
        document.querySelector('.filter-product-form-menu')?.getBoundingClientRect()?.width ?? 0

      const categoryLeft =
        document
          .querySelector(`.filter-product-form-menu-filterby-item-${name}`)
          ?.getBoundingClientRect()?.right ?? 0

      return { marginRight: `${parentWidth - categoryLeft}px` }
    }

    const left = document
      .querySelector(`.filter-product-form-menu-filterby-item-${name}`)
      ?.getBoundingClientRect()?.left

    return { marginLeft: `${left}px` }
  }, [isArabicStore, name])

  return (
    <ul className={`filter-product-form-fields filter-product-form-fields-${name}`} style={style}>
      {children}
    </ul>
  )
}

export type Filters = {
  aggregations: AggregationsFilters
  colorFilters: ColorFilter[]
  categoriesFilters: CategoriesFilters[]
  collectionFilters: CollectionProductFilter[]
  numberOfProduct: number
  allPrices: number[]
}

const FilterProductFormDesktop = ({
  aggregations,
  colorFilters,
  categoriesFilters,
  collectionFilters,
  numberOfProduct,
  allPrices,
}: Filters) => {
  const [activeOptionFilter, setActiveOptionFilter] = useState<string>()
  const { formState, onChangeFilter, onChangePriceFilter, onChangeSort, resetFilterForm } =
    useContext(ProductFilterFormContext)
  const onSelectOptionFilter = (name: string) => {
    setActiveOptionFilter((prevState) => (prevState !== name ? name : ''))
  }
  const t = useTranslation()
  const { algoliaConfig } = useContext(AlgoliaConfigContext)

  const minNumber = Math.min(...allPrices)
  const maxNumber = Math.max(...allPrices)

  const getSelectedFields = () => {
    const selectedFields: { id: string; label: string; onRemove: () => void }[] = []
    formState.colors.forEach((colorId) => {
      const color = colorFilters.find((colorFilter) => colorFilter.id === colorId)
      if (color) {
        selectedFields.push({
          id: `color-${color.id}`,
          label: color.label,
          onRemove: () => onChangeFilter('colors', color.id),
        })
      }
    })
    formState.collections.forEach((collectionId) => {
      const collection = collectionFilters.find(
        (collectionFilter) => collectionFilter.id === collectionId,
      )
      if (collection) {
        selectedFields.push({
          id: `collection-${collection.id}`,
          label: collection.label,
          onRemove: () => onChangeFilter('collections', collection.id),
        })
      }
    })
    formState.types.forEach((typeId) => {
      const type = categoriesFilters.find((typeFilter) => typeFilter.id === typeId)
      if (type) {
        selectedFields.push({
          id: `type-${type.id}`,
          label: type.label,
          onRemove: () => onChangeFilter('types', type.id),
        })
      }
    })

    const { min, max } = formState.priceRange

    if (min && max) {
      selectedFields.push({
        id: 'price-range',
        label: `${min} - ${max}`,
        onRemove: () => onChangePriceFilter({}),
      })
    }

    return selectedFields
  }

  const selectedFields = getSelectedFields()

  const currentLocale = useCurrentLocale()
  const isArabicLocale = getIsArabicLocale(currentLocale)

  return (
    <div className='filter-product-form-desktop'>
      <div className='filter-product-form-menu'>
        <FilterProductFormDesktopMenuFilterBy
          onClick={onSelectOptionFilter}
          aggregations={aggregations}
          activeOptionFilter={activeOptionFilter}
          categoriesFilters={categoriesFilters}
          collectionFilters={collectionFilters}
        />
        <div className='filter-product-form-menu-right'>
          {numberOfProduct < 2 ? (
            <p className='filter-product-form-nbarticle'>
              {numberOfProduct} {t('Item', {}, true)}
            </p>
          ) : (
            <p className='filter-product-form-nbarticle'>
              {numberOfProduct} {t('Items', {}, true)}
            </p>
          )}
          <FilterProductFormMenuOrderBy sortBy={formState.sortBy} onChange={onChangeSort} />
        </div>
      </div>

      <div className='filter-product-form-viewfields' data-open={!!activeOptionFilter}>
        {/* CATEGORIES */}
        {activeOptionFilter === 'type' && (
          <FilterProductFormFieldWrapper name='type'>
            {categoriesFilters.map((categorieFilter) => {
              const label = isArabicLocale
                ? `(${categorieFilter.numberOfOccurences}) ${categorieFilter.label}`
                : `${categorieFilter.label} (${categorieFilter.numberOfOccurences})`

              return (
                <InputCheckbox
                  key={categorieFilter.id}
                  id={categorieFilter.id.toString()}
                  className='filter-product-form-fields-item'
                  label={label}
                  value={categorieFilter.id}
                  checked={formState.types.includes(categorieFilter.id)}
                  onChange={() => {
                    onChangeFilter('types', categorieFilter.id)
                  }}
                />
              )
            })}
          </FilterProductFormFieldWrapper>
        )}

        {/* COLLECTION */}
        {activeOptionFilter === 'collection' && (
          <FilterProductFormFieldWrapper name='collection'>
            {collectionFilters.map((collectionFilter) => {
              const label = isArabicLocale
                ? `(${collectionFilter.numberOfOccurences}) ${collectionFilter.label}`
                : `${collectionFilter.label} (${collectionFilter.numberOfOccurences})`

              return (
                <InputCheckbox
                  key={collectionFilter.id}
                  id={collectionFilter.id.toString()}
                  className='filter-product-form-fields-item'
                  label={label}
                  value={collectionFilter.id}
                  checked={formState.collections.includes(collectionFilter.id)}
                  onChange={() => onChangeFilter('collections', collectionFilter.id)}
                />
              )
            })}
          </FilterProductFormFieldWrapper>
        )}

        {/* COLOR */}
        {activeOptionFilter === 'color' && (
          <FilterProductFormFieldWrapper name='color'>
            {colorFilters.map((colorFilter) => {
              const label = isArabicLocale
                ? `(${colorFilter.numberOfOccurences}) ${colorFilter.label}`
                : `${colorFilter.label} (${colorFilter.numberOfOccurences})`

              return (
                <InputCheckbox
                  key={colorFilter.id}
                  id={colorFilter.id.toString()}
                  className='filter-product-form-fields-item'
                  label={label}
                  value={colorFilter.id}
                  checked={formState.colors.includes(colorFilter.id)}
                  onChange={() => {
                    onChangeFilter('colors', colorFilter.id)
                  }}
                />
              )
            })}
          </FilterProductFormFieldWrapper>
        )}

        {/* PRICE */}
        {activeOptionFilter === 'price' && (
          <FilterProductFormPriceController
            minRange={minNumber}
            maxRange={maxNumber}
            initialMinValue={formState.priceRange.min}
            initialMaxValue={formState.priceRange.max}
            onChange={onChangePriceFilter}
            currency={algoliaConfig?.currencySymbol}
            isArabicLocale={isArabicLocale}
          />
        )}
      </div>

      {!!selectedFields.length && (
        <div className='filter-product-form-selectedfields'>
          <ul className='filter-product-form-selectedfields-list'>
            {selectedFields.map((field) => (
              <li key={field.id} className='filter-product-form-selectedfields-item'>
                <p>{field.label}</p>
                <button
                  onClick={field.onRemove}
                  type='button'
                  className='filter-product-form-selectedfields-item-close'>
                  <Icon iconName={Icons.CLOSE} />
                </button>
              </li>
            ))}
            <button
              onClick={resetFilterForm}
              type='button'
              className='filter-product-form-selectedfields-clear'>
              {t('Clear All', {}, true)}
            </button>
          </ul>
        </div>
      )}
    </div>
  )
}

export default FilterProductFormDesktop
